import React, { useEffect } from "react";
import { CheckCircle2, Loader2, AlertCircle, X, Circle, RotateCw } from "lucide-react";
import { useChatGatewaySyncStore, type SyncJob, type SyncStep } from "@/store/chatGatewaySyncStore";
import { sanitizeApiErrorMessage } from "@/utils/apiErrors";

const SUCCESS_AUTO_DISMISS_MS = 3_000;

const stepIcon = (status: SyncStep["status"]) => {
  switch (status) {
    case "done":
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case "running":
      return <Loader2 className="w-4 h-4 text-bridge-accent animate-spin" />;
    case "error":
      return <AlertCircle className="w-4 h-4 text-red-600" />;
    default:
      return <Circle className="w-4 h-4 text-bridge-muted/40" />;
  }
};

const jobBadge = (job: SyncJob) => {
  const hasError = job.steps.some((s) => s.status === "error");
  if (hasError) return { label: "Failed", cls: "bg-red-50 text-red-700 border-red-200" };
  if (job.finishedAt) return { label: "Synced", cls: "bg-green-50 text-green-700 border-green-200" };
  return { label: "Syncing…", cls: "bg-blue-50 text-blue-700 border-blue-200" };
};

const SyncJobPanel: React.FC<{ job: SyncJob }> = ({ job }) => {
  const dismissJob = useChatGatewaySyncStore((s) => s.dismissJob);
  const badge = jobBadge(job);
  const errorStep = job.steps.find((s) => s.status === "error");
  const hasError = job.steps.some((s) => s.status === "error");

  useEffect(() => {
    if (!job.finishedAt || hasError) return;

    const timer = window.setTimeout(() => dismissJob(job.id), SUCCESS_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [job.finishedAt, hasError, job.id, dismissJob]);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-bridge-border overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-bridge-border bg-slate-50/50">
              <div className="flex items-center gap-2 min-w-0">
                <RotateCw className="w-4 h-4 text-bridge-accent shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-bold text-bridge-fg truncate">
                    {job.targetKind === "BR" ? "Branch sync" : "Legal Entity sync"} · {job.targetName}
                  </div>
                  <div className="text-[10px] text-bridge-muted uppercase tracking-wider font-bold">
                    {job.subtitle}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-md border ${badge.cls}`}
                >
                  {badge.label}
                </span>
                {job.finishedAt && (
                  <button
                    onClick={() => dismissJob(job.id)}
                    className="p-1 text-bridge-muted hover:text-bridge-fg transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <ol className="px-4 py-3 space-y-2">
              {job.steps.map((step) => (
                <li key={step.id} className="flex items-center gap-3 text-sm">
                  {stepIcon(step.status)}
                  <span
                    className={
                      step.status === "done"
                        ? "text-bridge-fg"
                        : step.status === "running"
                          ? "text-bridge-fg font-medium"
                          : step.status === "error"
                            ? "text-red-700"
                            : "text-bridge-muted"
                    }
                  >
                    {step.label}
                  </span>
                </li>
              ))}
            </ol>
      {errorStep?.error && (
        <div className="px-4 pb-3 -mt-1 text-xs text-red-600 leading-relaxed">
          {sanitizeApiErrorMessage(errorStep.error)}
        </div>
      )}
    </div>
  );
};

/**
 * Global floating panel that mirrors active and recently-finished chat-gateway
 * sync jobs. Mounted in {@code AppLayout} so it survives route changes — the
 * user can leave the integrations page while a sync is in flight and still
 * see when it lands.
 */
export const ChatGatewaySyncProgress: React.FC = () => {
  const jobs = useChatGatewaySyncStore((s) => s.jobs);

  if (jobs.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 w-96 max-w-[calc(100vw-2rem)] space-y-3">
      {jobs.map((job) => (
        <SyncJobPanel key={job.id} job={job} />
      ))}
    </div>
  );
};